import React from 'react';
import * as echarts from 'echarts/core';
import ReactECharts from 'echarts-for-react';
import { LineChart } from 'echarts/charts';
import { GridComponent } from 'echarts/components';
import { Grid, GridSize } from '@material-ui/core';
import { createSpinner } from '../../../utils/spinnerManager';
import { IBalanceStorageResponse } from '../../../apis/vitusApiTypes';
import VChart from '../../../components/VChart/VChart';
import { handleApiError, months } from '../../../utils/common';
import AlertManager from '../../../utils/alertManager';
import messages from '../../../utils/messages';
import { colors, defaultLineRangeChartOptions, isRange, ITabProps, rangeLineColor, rangeLineOptions } from './gasBalanceUtils';
import { getBalanceStorage } from '../../../apis/vitusApi';

echarts.use(
    [LineChart, GridComponent]
);

interface IState {
    countriesData: IBalanceStorageResponse["success"]["storage"]["countries"],
}

class Storages extends React.Component<ITabProps, IState> {

    state: IState = {
        countriesData: [],
    }

    componentDidMount() {
        if (!this.props.data)
            this.getData();
        else
            this.storeData(this.props.data);
    }

    getData() {
        const spinner = createSpinner();

        getBalanceStorage().then(response => {
            if (response.data.success) {
                this.storeData(response.data.success);
                this.props.onDataChanged(response.data.success);
            }

            if (response.data.error)
                AlertManager.showError(messages.UNEXPECTED_ERROR_OCCURED); //TODO: message

        }).catch(error => {
            handleApiError(error);
        }).finally(() => {
            spinner.hide()
        });
    }

    storeData(response: IBalanceStorageResponse["success"]) {
        this.setState({
            countriesData: response.storage.countries || [],
        });
    }

    render() {
        return (
            <Grid container justify="flex-start" alignItems="flex-end">
                {
                    this.state.countriesData.map((country, idx) => {
                        return (
                            <ChartRow
                                colSpan={(this.state.countriesData.length % 2 === 1 && this.state.countriesData.length - 1 === idx) ? 12 : 6}
                                key={`row_${country.country_name}`}
                                country_name={country.country_name}
                                data={country.data}
                            />
                        );
                    })
                }
            </Grid>
        )
    }
}

type IChartRowProps = IBalanceStorageResponse["success"]["storage"]["countries"][0] & {
    colSpan: GridSize
};

class ChartRow extends React.Component<IChartRowProps, {}> {
    lineRangeChartRef: ReactECharts | null = null;

    componentDidMount() {
        if (this.props.data)
            this.updateLineChart();
    }

    componentDidUpdate(prevProps: IChartRowProps) {
        if (this.props.data && prevProps.data !== this.props.data)
            this.updateLineChart();
    }

    updateLineChart = () => {
        const lineChartInstance = this.lineRangeChartRef?.getEchartsInstance();
        lineChartInstance.showLoading();

        const data = this.props.data;

        lineChartInstance.clear();

        const lines = Object.keys(data[0]).filter(k => k !== "Month" && k !== "Day")

        const dataset = {
            dimensions: ['date_value', ...lines],
            source: data.map(originalRow => {
                const row = { ...originalRow };

                row["date_value"] = `${('0' + row.Month).slice(-2)}_${('0' + row.Day).slice(-2)}`;
                row["month"] = months[row.Month - 1];
                row["day"] = row.Day;
                return row;
            }),
        }

        lineChartInstance.setOption({
            ...defaultLineRangeChartOptions,
            color: colors.map((color, idx) => (isRange(lines[idx]) ? rangeLineColor : color)),
            series: lines.map(l => ({ type: 'line', ...(isRange(l) ? rangeLineOptions : {}) })),
            dataset: dataset,
            legend: {}
        });

        lineChartInstance.hideLoading();
    }

    render() {
        return (
            <Grid item xs={this.props.colSpan}>
                <VChart
                    title={!this.props.country_name ? "" : `${this.props.country_name} Total Storage by Year`}
                    height={350}
                    report={
                        <ReactECharts
                            ref={(e) => { this.lineRangeChartRef = e; }}
                            echarts={echarts}
                            option={defaultLineRangeChartOptions}
                            notMerge={true}
                            lazyUpdate={true}
                            style={{ width: '99%' }}
                        />
                    } >
                </VChart>
            </Grid>
        );
    }
}

export default Storages;